import React, { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { api, formatApiErrorDetail, fileToBase64 } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Camera, Upload, ArrowRight, Paperclip, Trash2, Loader2, FileText, CheckCircle2, ShieldCheck, Clock, ShieldAlert, X, UserCog } from "lucide-react";

/**
 * Simple "upload-only" step used for SLA / Sales Confirmation / Documentation.
 * For gated stages (buyer, sla, sales_confirmation), an Owner approval workflow is enforced.
 */
export default function SimpleUploadStep({
  data,
  reload,
  stage,
  stepNumber,
  title,
  description = "Upload the relevant documents for this step.",
  nextStageLabel = "next step",
  gated = false,
  requireCreditAnalysis = false,
}) {
  const { shipment, documents = [] } = data;
  const { user } = useAuth();
  const isOwner = !!user?.is_owner;
  const isAdmin = user?.role === "admin";

  const isActive = shipment.current_stage === stage;
  const isCompleted = (shipment.completed_stages || []).includes(stage);
  const stageDocs = documents.filter((d) => d.doc_type === "stage_inline" && d.stage === stage);
  const pendingApproval = shipment.pending_approval_stage === stage;
  const creditDone = !!shipment.credit_analysis_done;
  const stageApproval = shipment.stage_approvals?.[stage];

  const [docName, setDocName] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [advancing, setAdvancing] = useState(false);
  const [ownerPicker, setOwnerPicker] = useState(null); // { mode: 'submit'|'reroute', owners: [] }
  const fileRef = useRef(null);
  const cameraRef = useRef(null);

  const pick = (f) => {
    if (!f) return;
    setFile(f);
    if (!docName) setDocName(f.name.replace(/\.[^.]+$/, ""));
  };

  const upload = async (e) => {
    e?.preventDefault();
    if (!file) return toast.error("Pick a file first");
    if (!docName.trim()) return toast.error("Please enter a document name");
    setBusy(true);
    try {
      const b64 = await fileToBase64(file);
      const ext = (file.name.match(/\.[^.]+$/) || [""])[0];
      await api.post(`/shipments/${shipment.id}/stage-uploads`, {
        stage,
        filename: `${docName.trim()}${ext}`,
        content_base64: b64,
        mime_type: file.type || "application/octet-stream",
      });
      toast.success("Uploaded");
      setDocName(""); setFile(null);
      await reload();
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2?.response?.data?.detail) || e2.message);
    } finally { setBusy(false); }
  };

  const removeDoc = async (doc) => {
    const isGated = ["buyer", "sla", "sales_confirmation"].includes(stage);
    const wasApproved = !!stageApproval || (shipment.completed_stages || []).includes(stage);
    let prompt = `Remove ${doc.filename}?`;
    if (isGated && wasApproved) {
      prompt = `"${title}" was already approved.\n\nDeleting any approved document will:\n• Roll the shipment back to this step\n• Clear the existing approval\n• Lock all later steps\n\nYou'll need to request owner approval again.\n\nContinue?`;
    } else if (isGated && stageDocs.length === 1) {
      prompt = `This is the last document for "${title}". You'll need to re-upload before submitting for approval.\n\nContinue?`;
    }
    if (!window.confirm(prompt)) return;
    try {
      const r = await api.delete(`/shipments/${shipment.id}/stage-uploads/${doc.id}`);
      if (r.data?.reverted) {
        toast.warning(`Step rolled back — resubmit for owner approval`);
      } else {
        toast.success("Removed");
      }
      await reload();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
  };

  const toggleCredit = async () => {
    setBusy(true);
    try {
      await api.post(`/shipments/${shipment.id}/credit-analysis`, { completed: !creditDone });
      toast.success(!creditDone ? "Credit analysis marked complete" : "Credit analysis reopened");
      await reload();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const submitForApproval = async () => {
    setAdvancing(true);
    try {
      // Determine if owner picker is needed
      const owners = await fetchOwners();
      if (owners.length === 0) {
        toast.error("No active owners configured to receive approvals");
        return;
      }
      if (owners.length === 1) {
        await api.post(`/shipments/${shipment.id}/submit-for-approval`, { owner_id: owners[0].id });
        toast.success(`Submitted to ${owners[0].name || owners[0].email}`);
        await reload();
        return;
      }
      // Multi-owner -> open picker
      setOwnerPicker({ mode: "submit", owners });
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
    finally { setAdvancing(false); }
  };

  const reroute = async () => {
    try {
      const owners = await fetchOwners();
      const filtered = owners.filter((o) => o.id !== shipment.pending_approval_owner_id);
      if (filtered.length === 0) {
        toast.error("No other owners available to reroute to");
        return;
      }
      setOwnerPicker({ mode: "reroute", owners });
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
  };

  const fetchOwners = async () => {
    const r = await api.get("/owners");
    return r.data || [];
  };

  const confirmOwnerPick = async (ownerId) => {
    setAdvancing(true);
    try {
      const url = ownerPicker.mode === "reroute"
        ? `/shipments/${shipment.id}/reroute-approval`
        : `/shipments/${shipment.id}/submit-for-approval`;
      const r = await api.post(url, { owner_id: ownerId });
      const name = r.data?.assigned_owner?.name || r.data?.assigned_owner?.email || "owner";
      toast.success(ownerPicker.mode === "reroute" ? `Rerouted to ${name}` : `Submitted to ${name}`);
      setOwnerPicker(null);
      await reload();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
    finally { setAdvancing(false); }
  };

  const approve = async () => {
    setAdvancing(true);
    try {
      await api.post(`/shipments/${shipment.id}/approve`, {});
      toast.success("Approved — moved to next step");
      await reload();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
    finally { setAdvancing(false); }
  };

  const reject = async () => {
    if (!window.confirm("Send this back to the operator? They will need to resubmit.")) return;
    setAdvancing(true);
    try {
      await api.post(`/shipments/${shipment.id}/reject-approval`, {});
      toast.success("Returned to operator");
      await reload();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
    finally { setAdvancing(false); }
  };

  const proceed = async () => {
    if (stageDocs.length === 0 && !window.confirm("No documents uploaded yet. Proceed anyway?")) return;
    setAdvancing(true);
    try {
      await api.post(`/shipments/${shipment.id}/advance-stage`, {});
      toast.success("Moved to the next step");
      await reload();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
    finally { setAdvancing(false); }
  };

  // UI helpers
  const showUploadForm = isActive && !pendingApproval && isAdmin;
  const showCreditToggle = requireCreditAnalysis && isActive && !pendingApproval && isAdmin;
  const canDelete = isOwner;

  return (
    <div>
      <div className="text-xs font-bold uppercase tracking-widest text-stone-400">Step {stepNumber}</div>
      <h2 className="font-display text-xl font-bold tracking-tight text-[#1F1F1F] mt-1">{title}</h2>
      <p className="text-sm text-stone-500 mb-4">
        {description}
        {isCompleted && <span className="ml-1 text-[#6B0F1A] font-semibold">Completed ✓</span>}
      </p>

      {/* Approval status banner */}
      {gated && isActive && pendingApproval && (
        <div className="mb-4 rounded-xl bg-amber-50 border border-amber-200 px-4 py-3" data-testid={`approval-pending-${stage}`}>
          <div className="flex items-start gap-2">
            <Clock className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-amber-900">
                {shipment.pending_approval_owner_name
                  ? `Awaiting approval from ${shipment.pending_approval_owner_name}`
                  : "Awaiting owner approval"}
              </div>
              <div className="text-xs text-amber-800 mt-0.5">
                {shipment.approval_submitted_by_name ? `Submitted by ${shipment.approval_submitted_by_name}` : "Submitted"}
                {shipment.approval_submitted_at && ` · ${new Date(shipment.approval_submitted_at).toLocaleString()}`}
                {shipment.approval_rerouted_at && (
                  <span className="block text-[11px] text-amber-700 mt-0.5">
                    Rerouted{shipment.approval_rerouted_by_name ? ` by ${shipment.approval_rerouted_by_name}` : ""} · {new Date(shipment.approval_rerouted_at).toLocaleString()}
                  </span>
                )}
              </div>
            </div>
            {isAdmin && (
              <button
                onClick={reroute}
                className="inline-flex items-center gap-1 text-[11px] font-semibold bg-white border border-amber-300 text-amber-800 hover:bg-amber-100 rounded-lg px-2.5 py-1.5 shrink-0"
                data-testid={`reroute-approval-${stage}`}
              >
                <UserCog className="w-3 h-3" /> Reroute
              </button>
            )}
          </div>
        </div>
      )}
      {gated && stageApproval && (
        <div className="mb-4 rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-2.5 text-xs text-emerald-800 flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 shrink-0" />
          Approved by {stageApproval.approved_by_name} · {new Date(stageApproval.approved_at).toLocaleString()}
        </div>
      )}

      {/* Owner approval actions */}
      {gated && isActive && pendingApproval && isOwner && (() => {
        const assignedId = shipment.pending_approval_owner_id;
        const isAssigned = !assignedId || assignedId === user?.id;
        if (!isAssigned) {
          return (
            <div className="mb-4 rounded-xl bg-stone-50 border border-stone-200 px-4 py-2.5 text-xs text-stone-600 flex items-center gap-2">
              <Clock className="w-3.5 h-3.5 shrink-0" />
              This approval is routed to <strong className="text-[#6B0F1A]">{shipment.pending_approval_owner_name || "another owner"}</strong>. Ask the operator to reroute it to you if needed.
            </div>
          );
        }
        return (
          <div className="mb-4 flex flex-wrap gap-2">
            <button
              onClick={approve}
              disabled={advancing}
              className="inline-flex items-center gap-1.5 bg-[#6B0F1A] hover:bg-[#56121C] text-white text-sm font-semibold rounded-xl px-4 py-2.5 disabled:opacity-50"
              data-testid={`approve-${stage}`}
            >
              {advancing ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
              Approve & advance
            </button>
            <button
              onClick={reject}
              disabled={advancing}
              className="inline-flex items-center gap-1.5 bg-white border border-amber-300 text-amber-800 hover:bg-amber-50 text-sm font-semibold rounded-xl px-4 py-2.5 disabled:opacity-50"
              data-testid={`reject-${stage}`}
            >
              <X className="w-4 h-4" /> Send back
            </button>
          </div>
        );
      })()}

      {/* Credit analysis (buyer only) */}
      {showCreditToggle && (
        <div className={`mb-4 rounded-xl border p-3 flex items-center gap-3 ${creditDone ? "bg-emerald-50 border-emerald-200" : "bg-stone-50 border-stone-200"}`}>
          <ShieldAlert className={`w-5 h-5 shrink-0 ${creditDone ? "text-emerald-600" : "text-stone-500"}`} />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-[#1F1F1F]">Third-party credit analysis</div>
            <div className="text-[11px] text-stone-500">
              {creditDone ? "Completed by operator" : "Required before submission"}
            </div>
          </div>
          <button
            onClick={toggleCredit}
            disabled={busy}
            className={`text-xs font-semibold rounded-lg px-3 py-1.5 ${creditDone ? "bg-white border border-emerald-300 text-emerald-700 hover:bg-emerald-50" : "bg-[#6B0F1A] text-white hover:bg-[#56121C]"} disabled:opacity-50`}
            data-testid="credit-analysis-toggle"
          >
            {creditDone ? "Mark incomplete" : "Mark completed"}
          </button>
        </div>
      )}

      {/* Upload form */}
      {showUploadForm && (
        <form onSubmit={upload} className="bg-[#FBFAF9] border border-stone-200 rounded-xl p-4 space-y-3" data-testid={`simple-upload-${stage}`}>
          <label className="block">
            <span className="text-[11px] font-bold uppercase tracking-widest text-stone-500">Document name</span>
            <input
              required
              value={docName}
              onChange={(e) => setDocName(e.target.value)}
              placeholder={stage === "buyer" ? "e.g. Trade License, Passport copy, Bank Letter…" : "e.g. Signed SLA, Sales Confirmation…"}
              className="mt-1 w-full bg-white border border-stone-300 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#A8323F] focus:ring-2 focus:ring-[#A8323F]/15"
              data-testid={`upload-name-${stage}`}
            />
          </label>

          {file ? (
            <div className="flex items-center gap-2 bg-white border border-stone-200 rounded-lg px-3 py-2">
              <FileText className="w-4 h-4 text-[#6B0F1A] shrink-0" />
              <span className="text-sm truncate flex-1">{file.name}</span>
              <span className="text-[10px] text-stone-400">{Math.round(file.size / 1024)} KB</span>
              <button type="button" onClick={() => setFile(null)} className="text-stone-400 hover:text-red-600">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              <button type="button" onClick={() => cameraRef.current?.click()} className="rounded-lg border-2 border-dashed border-stone-300 py-3 text-xs text-stone-600 hover:border-[#A8323F] hover:text-[#6B0F1A] inline-flex items-center justify-center gap-1.5" data-testid={`upload-camera-${stage}`}>
                <Camera className="w-4 h-4" /> Camera
              </button>
              <button type="button" onClick={() => fileRef.current?.click()} className="rounded-lg border-2 border-dashed border-stone-300 py-3 text-xs text-stone-600 hover:border-[#A8323F] hover:text-[#6B0F1A] inline-flex items-center justify-center gap-1.5" data-testid={`upload-file-${stage}`}>
                <Upload className="w-4 h-4" /> Pick file
              </button>
            </div>
          )}

          <button type="submit" disabled={busy || !file || !docName.trim()} className="inline-flex items-center gap-1.5 bg-[#6B0F1A] hover:bg-[#56121C] text-white text-sm font-semibold rounded-xl px-4 py-2.5 disabled:opacity-50" data-testid={`upload-submit-${stage}`}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {busy ? "Uploading…" : "Upload document"}
          </button>

          <input ref={cameraRef} type="file" accept="image/*" capture="environment" hidden onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ""; }} />
          <input ref={fileRef} type="file" hidden onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ""; }} />
        </form>
      )}

      {/* Existing docs list */}
      <div className="mt-4">
        <div className="text-[11px] font-bold uppercase tracking-widest text-stone-500 mb-2">
          Uploaded documents <span className="text-stone-400">({stageDocs.length})</span>
        </div>
        {stageDocs.length === 0 ? (
          <div className="text-xs text-stone-400 italic">No documents uploaded yet.</div>
        ) : (
          <ul className="space-y-1.5" data-testid={`uploaded-list-${stage}`}>
            {stageDocs.map((d) => (
              <li key={d.id} className="bg-white border border-stone-200 rounded-lg px-3 py-2 flex items-center gap-2">
                <Paperclip className="w-3.5 h-3.5 text-[#6B0F1A] shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-[#1F1F1F] truncate">{d.filename}</div>
                  <div className="text-[10px] text-stone-400">{new Date(d.uploaded_at).toLocaleString()}{d.uploaded_by_name ? ` · ${d.uploaded_by_name}` : ""}</div>
                </div>
                {canDelete && (
                  <button onClick={() => removeDoc(d)} className="text-stone-400 hover:text-red-600 shrink-0" data-testid={`uploaded-delete-${d.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Action buttons */}
      {isActive && isAdmin && (
        <div className="mt-4 flex flex-wrap gap-2">
          {gated ? (
            !pendingApproval && (
              <button
                onClick={submitForApproval}
                disabled={advancing}
                className="inline-flex items-center gap-1.5 bg-[#6B0F1A] hover:bg-[#56121C] text-white text-sm font-semibold rounded-xl px-4 py-2.5 disabled:opacity-50"
                data-testid={`submit-for-approval-${stage}`}
              >
                {advancing ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
                Submit for owner approval
              </button>
            )
          ) : (
            <button
              onClick={proceed}
              disabled={advancing}
              className="inline-flex items-center gap-1.5 bg-[#6B0F1A] hover:bg-[#56121C] text-white text-sm font-semibold rounded-xl px-4 py-2.5 disabled:opacity-50"
              data-testid={`advance-${stage}`}
            >
              {advancing ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              {advancing ? "Advancing…" : `Proceed to ${nextStageLabel}`}
              <ArrowRight className="w-4 h-4" />
            </button>
          )}
        </div>
      )}

      {/* Owner picker modal */}
      {ownerPicker && createPortal(
        <div className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center p-4 bg-black/50" onClick={() => setOwnerPicker(null)} data-testid="owner-picker-modal">
          <div className="bg-white w-full max-w-md rounded-2xl p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3 mb-1">
              <h3 className="font-display text-lg font-extrabold text-[#1F1F1F]">
                {ownerPicker.mode === "reroute" ? "Reroute approval" : "Send approval to"}
              </h3>
              <button onClick={() => setOwnerPicker(null)} className="text-stone-400 hover:text-stone-600" aria-label="Close">
                <X className="w-4 h-4" />
              </button>
            </div>
            <p className="text-xs text-stone-500 mb-4">
              {ownerPicker.mode === "reroute"
                ? "Pick the correct owner to receive this approval."
                : "Multiple owners are configured. Pick who should receive this approval request."}
            </p>
            <div className="space-y-2 max-h-[50vh] overflow-y-auto">
              {ownerPicker.owners.map((o) => {
                const isCurrent = o.id === shipment.pending_approval_owner_id;
                const disabled = ownerPicker.mode === "reroute" && isCurrent;
                return (
                  <button
                    key={o.id}
                    onClick={() => !disabled && confirmOwnerPick(o.id)}
                    disabled={disabled || advancing}
                    className={`w-full text-left flex items-center gap-3 rounded-xl border px-3 py-3 transition-colors ${
                      disabled ? "bg-stone-50 border-stone-200 opacity-50 cursor-not-allowed" : "bg-white border-stone-200 hover:border-[#6B0F1A] hover:bg-[#F4E8E9]/40"
                    }`}
                    data-testid={`owner-option-${o.id}`}
                  >
                    <div className="w-9 h-9 rounded-full bg-[#F4E8E9] text-[#6B0F1A] flex items-center justify-center font-bold text-sm">
                      {(o.name || o.email || "?").trim().charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-[#1F1F1F] truncate">
                        {o.name || o.email}
                        {o.is_self && <span className="ml-1.5 text-[10px] uppercase tracking-wider text-stone-400">(you)</span>}
                      </div>
                      <div className="text-[11px] text-stone-500 truncate">{o.email}</div>
                    </div>
                    {isCurrent && (
                      <span className="text-[10px] font-bold uppercase tracking-wider text-amber-700 bg-amber-100 border border-amber-200 rounded-full px-2 py-0.5">Current</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
